// Shared product pricing, sizing and stock helpers.
// Used by BrowseProducts, ProductPage, CartPage and HarvestStrip.
// Single source of truth - edit here, never copy this maths into a page file.

// Trading Standards requires a 20g minimum order size on every product - the
// smallest value in each preset below must never drop under 20.
export const SIZE_PRESETS = {
  smallHerbs:  [20, 25, 50],
  herbs:       [50, 100],
  leafy:       [50, 100, 200],
  microgreens: [20, 30, 50, 100],
  default:     [30, 50, 100],
};

// Returns the correct gram size options for a given product.
export function sizeProfileFor(product) {
  const name     = String(product?.name     || '').toLowerCase();
  const category = String(product?.category || '').toLowerCase();
  if (name.includes('chive') || name.includes('dill') || name.includes('coriander'))
    return SIZE_PRESETS.smallHerbs;
  if (category.includes('herbs'))    return SIZE_PRESETS.herbs;
  if (category.includes('lettuce') || category.includes('komatsuna') || category.includes('salads'))
    return SIZE_PRESETS.leafy;
  if (category.includes('micro') || category.includes('cress')) return SIZE_PRESETS.microgreens;
  return SIZE_PRESETS.default;
}

// Smallest pack a customer can buy for this product.
export function minPackGrams(product) {
  return sizeProfileFor(product)[0];
}

// Price multiplier: smaller packs cost more per gram, and every step up is
// cheaper per gram than the one below it.
export function packMultiplier(weight) {
  if (weight <= 20)  return 1.25;
  if (weight <= 30)  return 1.15;
  if (weight <= 50)  return 1.1;
  if (weight <= 100) return 1.0;
  return 0.92;
}

const roundUp10p   = x => Math.ceil(x * 10 - 1e-6) / 10;
const roundDown10p = x => Math.floor(x * 10 + 1e-6) / 10;
const rawPackPrice = (product, grams) =>
  grams * (Number(product?.price) / 100) * packMultiplier(grams);

// Price of one pack in pounds, rounded to 10p so prices look set, not
// calculated. product.price is the per-100g base price.
// Every price shown or added to the cart must come from here.
//
// Packs round UP, except where that would make a bigger pack dearer per gram
// than the size below it (it can happen on small packs at low base prices,
// e.g. chives at £2.79/100g: 20g 70p, 25g would be 90p). That pack rounds
// DOWN instead, so bigger is always better value.
export function packPrice(product, grams) {
  const sizes = sizeProfileFor(product);
  if (!sizes.includes(grams)) return roundUp10p(rawPackPrice(product, grams));
  let prevPerGram = Infinity;
  for (const g of sizes) {
    const raw = rawPackPrice(product, g);
    let price = roundUp10p(raw);
    if (price / g > prevPerGram + 1e-9) price = roundDown10p(raw);
    if (g === grams) return price;
    prevPerGram = price / g;
  }
}

// Every pack size for a product with its price, per-100g price and whether
// there is enough stock to sell it.
export function packOptionsFor(product, availableGrams = getStock(product)) {
  if (!product?.price) return [];
  return sizeProfileFor(product).map(grams => {
    const price = packPrice(product, grams);
    return {
      grams,
      price,
      per100g:   Math.round((price / grams) * 100 * 100) / 100,
      available: grams <= availableGrams,
    };
  });
}

// The "from £X.XX" price shown on cards: the smallest pack.
// price is a string with two decimals, ready to display.
export function getStartingPrice(product) {
  const grams = minPackGrams(product);
  return { price: packPrice(product, grams).toFixed(2), grams };
}

// Reads stock regardless of which field a product uses (handles legacy schema).
export function getStock(product) {
  if (product?.stock     != null) return Number(product.stock);
  if (product?.stockGrams != null) return Number(product.stockGrams);
  if (product?.quantity   != null) return Number(product.quantity);
  return 0;
}

// AVAILABLE only when there is enough stock for at least the smallest pack.
// 10g left on a product whose smallest pack is 20g is SOLD_OUT, because
// nobody can buy it.
export function getStockStatus(product) {
  const s = getStock(product);
  if (s < 0) return 'COMING_SOON';
  return s >= minPackGrams(product) ? 'AVAILABLE' : 'SOLD_OUT';
}

// "Only 40g left" when a product can still be bought but has under
// LOW_STOCK_GRAMS left, otherwise null. Shown on the browse cards and the
// product page.
export const LOW_STOCK_GRAMS = 50;
export function lowStockLabel(product) {
  const s = getStock(product);
  return getStockStatus(product) === 'AVAILABLE' && s < LOW_STOCK_GRAMS ? `Only ${s}g left` : null;
}
